import type { ShellNavigationIntent } from '@/core/lib/shell-navigation-intent';
import { shellPaths } from '@/core/router/app-paths';

export type ProductSetupSuccessNavigation =
  | { kind: 'popStack'; fallbackPath: string }
  | {
      kind: 'navigate';
      path: string;
      intent: Extract<ShellNavigationIntent, 'replaceFlat'>;
    };

export type ApplyProductSetupSuccessLeaveDeps = {
  canGoBack: boolean;
  goBack: () => void;
  navigate: (
    path: string,
    options: {
      intent: Extract<ShellNavigationIntent, 'switchTab' | 'replaceFlat'>;
    },
  ) => void;
};

/**
 * Where to go after a product/card is created.
 *
 * Profile card linking is pushed onto the profiles tab. Ionic `replace` (root
 * or forward) does not pop that view, so the wizard stays on screen. Pop with
 * `goBack`, same as the page header. `resetTab` is only the no-history fallback
 * and must run after the mutation callback (macrotask), or Query treats unmount
 * as failure.
 */
export function resolveProductSetupSuccessNavigation(params: {
  isProfileBcMode: boolean;
  firstProductId?: string | null;
}): ProductSetupSuccessNavigation {
  if (params.isProfileBcMode) {
    return { kind: 'popStack', fallbackPath: shellPaths.profiles };
  }
  const id = params.firstProductId?.trim();
  if (id) {
    return {
      kind: 'navigate',
      path: shellPaths.moreProductDetail(id),
      intent: 'replaceFlat',
    };
  }
  return {
    kind: 'navigate',
    path: shellPaths.moreProducts,
    intent: 'replaceFlat',
  };
}

export function applyProductSetupSuccessLeave(
  next: ProductSetupSuccessNavigation,
  deps: ApplyProductSetupSuccessLeaveDeps,
): void {
  if (next.kind === 'popStack') {
    if (deps.canGoBack) {
      deps.goBack();
      return;
    }
    deps.navigate(next.fallbackPath, { intent: 'switchTab' });
    return;
  }
  deps.navigate(next.path, { intent: next.intent });
}
